import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import SocialShare from './SocialShare';
import { PageHero, RichText } from './PageLayout';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    wrapper: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(8),
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(5),
        },
    },
    figure: {
        margin: `0 0 ${theme.spacing(4)}px`,
    },
    image: {
        display: 'block',
        width: '100%',
        height: 'auto',
        borderRadius: tokens.radius,
        border: `1px solid ${tokens.border}`,
    },
    caption: {
        marginTop: theme.spacing(1),
        color: tokens.muted,
        fontSize: '0.875rem',
    },
    aside: {
        position: 'sticky',
        top: theme.spacing(12),
    },
    panel: {
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        backgroundColor: tokens.surface,
    },
    panelTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: tokens.muted,
        margin: `0 0 ${theme.spacing(2)}px`,
    },
    detailRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingBottom: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${tokens.border}`,
        '&:last-child': {
            borderBottom: 'none',
            paddingBottom: 0,
            marginBottom: 0,
        },
    },
    detailLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: tokens.muted,
    },
    detailValue: {
        fontSize: '0.9375rem',
        color: tokens.ink,
        fontWeight: 500,
        wordBreak: 'break-word',
    },
}));

/**
 * Standard detail-page shell: institutional banner, a readable article column
 * and an optional sidebar with metadata, downloads and share links.
 *
 * `details` is a list of `{ label, value }` pairs; falsy values are skipped.
 */
export default function ArticleLayout({
    eyebrow,
    title,
    description,
    breadcrumbs,
    actions,
    image,
    imageAlt,
    imageCaption,
    body,
    contentClassName = '',
    details = [],
    shareUrl,
    sidebar,
    children,
}) {
    const classes = useStyles();
    const visibleDetails = details.filter((item) => item && item.value);

    return (
        <>
            <PageHero
                eyebrow={eyebrow}
                title={title}
                description={description}
                breadcrumbs={breadcrumbs}
                actions={actions}
                compact
            />

            <div className={classes.wrapper}>
                <Container maxWidth="lg">
                    <Grid container spacing={6}>
                        <Grid item xs={12} md={8}>
                            {image && (
                                <figure className={classes.figure}>
                                    <img
                                        className={classes.image}
                                        src={image}
                                        alt={imageAlt || title || ''}
                                        loading="lazy"
                                    />
                                    {imageCaption && (
                                        <figcaption className={classes.caption}>{imageCaption}</figcaption>
                                    )}
                                </figure>
                            )}

                            <RichText html={body} className={contentClassName} />
                            {children}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <div className={classes.aside}>
                                {visibleDetails.length > 0 && (
                                    <div className={classes.panel}>
                                        <p className={classes.panelTitle}>Details</p>
                                        {visibleDetails.map((item) => (
                                            <div className={classes.detailRow} key={item.label}>
                                                <span className={classes.detailLabel}>{item.label}</span>
                                                <span className={classes.detailValue}>{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {sidebar}

                                {shareUrl && (
                                    <div className={classes.panel}>
                                        <p className={classes.panelTitle}>Share this page</p>
                                        <SocialShare url={shareUrl} title={title} size={32} />
                                    </div>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </Container>
            </div>
        </>
    );
}

/** Message shown while a detail page is still resolving its entry. */
export function ArticleFallback({ title, description }) {
    const classes = useStyles();

    return (
        <div className={classes.wrapper}>
            <Container maxWidth="md">
                <Typography variant="h4" component="h1" gutterBottom>
                    {title}
                </Typography>
                <p style={{ color: tokens.inkSoft, lineHeight: 1.7 }}>{description}</p>
            </Container>
        </div>
    );
}
